import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {
  branches,
  brands,
  reservationSources,
  vehicleGroups,
} from '../../../../shared/models/SharedApiLookups'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import {
  initialOffer,
  IOfferModel,
  OFFER_TABS,
  OfferSaleStatus,
  OfferStatus,
  offerValidation,
  OperationType,
} from './OfferModel'
import {createOffer, deleteOffer, getAllOffers, updateOffer} from './Api'
import {OfferTable} from './OfferTable'

export const OfferPage: FC = () => {
  const [tab, setTabState] = useState<'form' | 'list'>('form')
  const [offer, setOffer] = useState<IOfferModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [offers, setOffers] = useState<{value: string; label: string}[]>([])
  const [brandOptions, setBrandOptions] = useState<{value: string; label: string}[]>([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [reservationSourcesOptions, setReservationSourcesOptions] = useState<{value: string; label: string}[]>([])

  
  const setSelectedIdFromHeader = useCallback((id: string | null) => {
    setSelectedId(id ? Number(id) : null)
  }, [])


  useEffect(() => {
    Promise.all([vehicleGroups(), brands(), branches(), reservationSources()]).then(([vg, br, brn, rs]) => {
      setOffers(vg)
      setBrandOptions(br)
      setBranchOptions(brn)
      setReservationSourcesOptions(rs)
    })
  }, [])

  const fetchOffers = useCallback(
    async (page: number = 0, _sort?: string, _order?: 'asc' | 'desc') => {
      try {
        setLoading(true)
        const res = await getAllOffers(page)
        const data = res.data.payload || res.data
        setOffer(data.content || data || [])
        setTotalPages(data.totalPages || 1)
      } catch (err: any) {
        if (err?.response?.status === 401) {
          window.location.href = '/login'
        }
        console.error(err)
        showErrorToast('Kayıtlar getirilirken hata oluştu.')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    fetchOffers(currentPage, sortBy, sortOrder)
  }, [fetchOffers, currentPage, sortBy, sortOrder])

  const handleSave = async (values: IOfferModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId != null) {
        await updateOffer({...values, id: selectedId})
        showSuccessToast('Başarıyla güncellendi')
      } else {
        await createOffer(values)
        showSuccessToast('Başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchOffers(0, sortBy, sortOrder)
      resetForm()
      setSelectedId(null)
      setTabState('list') 
    } catch (e) {
      console.error(e)
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (row: IOfferModel, formik: FormikProps<IOfferModel>) => {
    formik.setValues(row)
    setSelectedId(row.id)
    setTabState('form')
  }

  const handleDeleteClick = useCallback((row: IOfferModel) => {
    setSelectedId(row.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId == null) return
    try {
      setLoading(true)
      await deleteOffer(selectedId)
      showSuccessToast('Başarıyla silindi')
      await fetchOffers(currentPage)
    } catch (err) {
      console.error(err)
      showErrorToast('Silinirken bir hata oluştu')
    } finally {
      setShowModal(false)
      setSelectedId(null)
      setLoading(false)
    }
  }


  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchOffers(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchOffers]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchOffers(currentPage, column, order)
    },
    [currentPage, fetchOffers]
  )


  return (
    <>
      <Formik
        initialValues={initialOffer}
        validationSchema={offerValidation}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='offerForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={(next: string) => setTabState((next === 'list' ? 'list' : 'form'))}
                setSelectedId={setSelectedIdFromHeader}
                tabs={OFFER_TABS}
                onExportExcel={() => console.log('Excel export')}
              />

          
              {tab === 'form' && (
                <div className='card'>
                  <div className='card-body'>
                    <div className='mb-4'>
                      <h5 className='card-title mb-0'>Kampanya Fiyat Detayı</h5>
                    </div>

                    <Form id='offerForm' className='form'>
                      <div className='row mb-6'>
                        <SelectInput
                          name='offerStatus'
                          label='Durum'
                          options={[
                            {label: 'Aktif', value: OfferStatus.AKTIF},
                            {label: 'Pasif', value: OfferStatus.PASIF},
                          ]}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='offerSaleStatus'
                          label='Satış Durumu'
                          options={[
                            {label: 'Satışa Açık', value: OfferSaleStatus.SATISA_ACIK},
                            {label: 'Satışa Kapalı', value: OfferSaleStatus.SATISA_KAPALI},
                          ]}
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row mb-6'>
                        <SelectInput
                          name='reservationSource'
                          label='Rezervasyon Kaynağı'
                          options={reservationSourcesOptions}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='locations'
                          label='Lokasyonlar'
                          options={branchOptions}
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row mb-6'>
                        <TextInput
                          name='startDate'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='endDate'
                          type='date'
                          label='Bitiş Tarihi'
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row mb-6'>
                        <SelectInput
                          name='operation'
                          label='İşlem'
                          options={[
                            {label: '% Azalt', value: OperationType.AZALT},
                            {label: '% Arttır', value: OperationType.ARTTIR},
                          ]}
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='operationValue'
                          type='number'
                          label='%'
                          placeholder='0'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='minDay'
                          type='number'
                          label='Min Gün'
                          placeholder='0'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='maxDay'
                          type='number'
                          label='Max Gün'
                          placeholder='999'
                          wrapperClassName='col-md-3'
                        />
                      </div>
                    </Form>
                  </div>
                </div>
              )}

             
              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h5 className='mb-0 text-uppercase'>KAMPANYA LİSTESİ</h5>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-light-primary'
                          onClick={() => {
                            setTabState('form')
                            formik.resetForm()
                            setSelectedId(null)
                          }}
                        >
                          Yeni Kayıt
                        </button>
                        <button
                          type='button'
                          className='btn btn-light'
                          onClick={() => fetchOffers(0, sortBy, sortOrder)}
                        >
                          Yenile
                        </button>
                      </div>
                    </div>

                    <OfferTable
                      data={offer}
                      loading={loading}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      onEdit={(row: IOfferModel) => handleEdit(row, formik)}
                      onDelete={handleDeleteClick}
                    />

                    {(!offer || offer.length === 0) && !loading && (
                      <div className='text-muted small mt-3'>Kayıt bulunamadı.</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu teklifi silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
